import PaymentForm from '@/components/dashboard/payment/PaymentForm';
import { CenteredSpinner } from '@/components/shared/Spinner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAuthWithProfile } from '@/hooks/useAuth';
import { useDashboardProperty } from '@/hooks/useDashboardProperty';
import { cn } from '@/lib/utils';
import { useSupabaseFunction } from '@/services/supabase';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Building2, CheckCircle, ChevronRight, CreditCard, Info } from 'lucide-react';
import { Suspense, useEffect, useState } from 'react';

export const Route = createFileRoute('/_protected/dashboard/payment')({
  component: PaymentPage,
})

const pricingPlans = [
  {
    name: 'Core',
    description: 'Perfect for tech-savvy sellers who want to do it themselves',
    price: '$599',
    priceId: 'price_1QxvHDKr3CmU9lCQF7ONyDOy',
    features: [
      '24/7 AI Agent Support',
      'MLS Listing',
      'Digital Document Management',
      'Showings Scheduler',
      'AI-guided closing support'
    ]
  },
  {
    name: 'Premium',
    description: 'For sellers who want to maximize their listing potential',
    price: '$1,499',
    priceId: 'price_1QxvRtKr3CmU9lCQBpOxBo1h',
    highlighted: true,
    features: [
      'Everything in Core, plus:',
      'Professional Photography Session',
      'Lock Box and For Sale Yard Sign',
      'Comparative Market Analysis',
      'Premium AI Support with Priority Response'
    ]
  }
];

function PaymentPage() {
  const { property } = useDashboardProperty();
  const navigate = useNavigate();
  const { profile } = useAuthWithProfile();

  const [tab, setTab] = useState(property.plan ? "confirmation" : "plan");
  const [selectedPlan, setSelectedPlan] = useState<typeof pricingPlans[0]>(pricingPlans[0]);
  const discordNotify = useSupabaseFunction("discord-notify");

  const handlePaymentComplete = async () => {
    discordNotify.mutateAsync({
      message: `Payment received`,
      data: {
        userId: profile.user_id,
        email: profile.email,
      }
    });
    setTab("confirmation");
  };

  useEffect(() => {
    if (property.plan) {
      setTab("confirmation");
    }
  }, [property.plan]);

  return (
    <div className="space-y-8">
      <h1 className="text-3xl font-bold">Complete Your Payment</h1>
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsContent value="plan">
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {pricingPlans.map((plan) => (
                <Card
                  key={plan.name}
                  className={cn(
                    "relative transition-all cursor-pointer hover:border-blue-300",
                    selectedPlan?.priceId === plan.priceId && "border-blue-500 ring-2 ring-blue-500/20",
                    plan.highlighted && "bg-linear-to-br from-blue-900 to-blue-800 text-white"
                  )}
                  onClick={() => setSelectedPlan(plan)}
                >
                  {selectedPlan?.priceId === plan.priceId && (
                    <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs py-1 px-3 rounded-bl-md">
                      Selected
                    </div>
                  )}

                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className={cn("text-2xl font-bold", plan.highlighted && "text-white")}>
                        <span className="flex items-center gap-2">
                          <Building2 className={cn("h-5 w-5", plan.highlighted ? "text-blue-300" : "text-blue-600")} />
                          {plan.name}
                        </span>
                      </CardTitle>
                      <span className={cn("text-3xl font-bold", plan.highlighted ? "text-white" : "text-blue-600")}>
                        {plan.price}
                      </span>
                    </div>
                    <p className={cn("text-sm mt-2", plan.highlighted ? "text-blue-100" : "text-muted-foreground")}>
                      {plan.description}
                    </p>
                  </CardHeader>

                  <CardContent>
                    <div className="space-y-3">
                      {plan.features.map((feature, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <CheckCircle className={cn("h-5 w-5 mt-0.5", plan.highlighted ? "text-blue-300" : "text-blue-600")} />
                          <span className={cn("text-sm", plan.highlighted && "text-blue-50")}>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>

                  <CardFooter>
                    <div className="w-full flex items-center justify-between">
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                              <Info className={cn("h-4 w-4", plan.highlighted ? "text-blue-200" : "text-muted-foreground")} />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p>Zero listing commission, still subject to buyer's agent fees (typically 1-2.5%)</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <Button
                        variant={plan.highlighted ? "secondary" : "default"}
                        className={cn(
                          plan.highlighted
                            ? "bg-white text-blue-900 hover:bg-blue-50"
                            : "bg-blue-700 hover:bg-blue-800 text-white"
                        )}
                        onClick={e => {
                          e.stopPropagation();
                          setSelectedPlan(plan);
                        }}
                      >
                        {selectedPlan?.priceId === plan.priceId ? "Selected" : "Select Plan"}
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              ))}
            </div>
            <div className="flex justify-end mt-8">
              <Button
                onClick={() => setTab("payment")}
                disabled={!selectedPlan}
                className="bg-blue-700 hover:bg-blue-800"
                size="lg"
              >
                Continue to Payment
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="payment">
          {selectedPlan && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-blue-600" />
                  Complete Payment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Suspense fallback={<CenteredSpinner />}>
                  <PaymentForm
                    priceId={selectedPlan.priceId}
                    metadata={{ property_id: property.property_id }}
                    onPaymentComplete={handlePaymentComplete}
                  />
                </Suspense>
              </CardContent>
              <CardFooter className="border-t flex justify-between pt-4">
                <Button variant="outline" onClick={() => setTab("plan")}>
                  Back to Plan Selection
                </Button>
                <span className="text-sm text-muted-foreground">
                  You won't be charged until you complete payment
                </span>
              </CardFooter>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="confirmation">
          <Card className="border border-green-100 bg-white">
            <CardContent className="pt-8">
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
                <p className="text-muted-foreground max-w-lg mx-auto mb-8">
                  Thank you for your payment. Your property listing is now moving to the next step.
                  Continue with identity verification to proceed with your listing.
                </p>
                <Button
                  className="bg-blue-700 hover:bg-blue-800"
                  onClick={() => navigate({ to: '/dashboard' })}
                >
                  Next Steps
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </CardContent>
            <CardFooter className="bg-muted border-t px-6 py-4">
              <div className="w-full flex items-center gap-2 justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="text-sm text-green-700">Secure payment processed</span>
              </div>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
